import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { CalendarRange, GraduationCap } from "lucide-react";

import { blockNoteToHtml } from "../../../../../shared/utils/blockNote";
import { SessionCourseState } from "../../../application/useSessionCourse";
import { CourseModuleOption } from "../../../domain/sessionExtras";

interface SessionCourseModuleProps {
    /** The page's course state: it also decides whether the tab exists. */
    course: SessionCourseState;
}

const SELECT_CLASS =
    "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-content-primary dark:border-gray-700 disabled:opacity-60";

function formatDay(day: string | null): string | null {
    if (!day) return null;
    const [year, month, date] = day.split("-");
    return `${date}-${month}-${year}`;
}

/**
 * The chosen module as it read when it was assigned — to read, never to edit
 * here. Later edits to the course plan do not reach a mentoring already given.
 */
function ModuleDetail({ module }: { module: CourseModuleOption }) {
    const { t } = useTranslation();
    const summary = useMemo(
        () => (module.summary ? DOMPurify.sanitize(blockNoteToHtml(module.summary)) : ""),
        [module.summary]
    );
    const from = formatDay(module.startsOn);
    const to = formatDay(module.endsOn);

    return (
        <article aria-labelledby="session-module-name" className="space-y-5">
            <header className="space-y-1">
                <h4 id="session-module-name" className="text-base font-semibold text-content-primary">
                    {module.name}
                </h4>
                {(from || to) && (
                    <p className="flex items-center gap-1.5 text-sm text-content-tertiary">
                        <CalendarRange className="h-4 w-4" aria-hidden="true" />
                        {[from, to].filter(Boolean).join(" – ")}
                    </p>
                )}
            </header>

            {module.description && (
                <p className="whitespace-pre-line text-sm leading-relaxed text-content-secondary">{module.description}</p>
            )}

            {module.topics.length > 0 && (
                <section aria-labelledby="session-module-topics">
                    <h5 id="session-module-topics" className="mb-2 text-xs font-semibold uppercase tracking-wider text-content-tertiary">
                        {t("session.course.topics")}
                    </h5>
                    <ul className="flex flex-wrap gap-1.5">
                        {module.topics.map((topic) => (
                            <li
                                key={topic}
                                className="rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-500/10 dark:text-brand-300"
                            >
                                {topic}
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <section aria-labelledby="session-module-summary">
                <h5 id="session-module-summary" className="mb-2 text-xs font-semibold uppercase tracking-wider text-content-tertiary">
                    {t("session.course.summary")}
                </h5>
                {summary.trim() ? (
                    <div className="notion-viewer notion-viewer-content" dangerouslySetInnerHTML={{ __html: summary }} />
                ) : (
                    <p className="text-sm italic text-content-tertiary">{t("session.course.noSummary")}</p>
                )}
            </section>
        </article>
    );
}

/**
 * A training centre's mentoring: its student's course, the module of it this
 * mentoring works on, and that module as the plan describes it. Renders
 * nothing for a session without a course.
 */
export default function SessionCourseModule({ course: state }: SessionCourseModuleProps) {
    const { t } = useTranslation();
    const { course, saving, saveFailed, assign } = state;

    if (!course?.course) return null;
    const selected = course.module;
    // A module since taken out of the plan is still this mentoring's.
    const options =
        selected && !course.modules.some((module) => module.id === selected.id)
            ? [selected, ...course.modules]
            : course.modules;

    return (
        <div className="space-y-6">
            <section
                aria-labelledby="session-course-title"
                className="glass-effect glass-border border rounded-2xl p-5 sm:p-6 shadow-theme-sm"
            >
                {/* The course on the left, the module chosen for this mentoring
                    on the right; one above the other on a phone. */}
                <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
                    <div className="min-w-0">
                        <h3
                            id="session-course-title"
                            className="mb-3 text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2"
                        >
                            <GraduationCap className="w-4 h-4 text-brand-500" aria-hidden="true" />
                            {t("session.course.title")}
                        </h3>
                        <p className="text-sm font-semibold text-content-primary">{course.course.name}</p>
                        <p className="text-xs text-content-tertiary">{course.course.internalName}</p>
                    </div>

                    <div className="w-full md:w-80 md:shrink-0">
                        <label htmlFor="session-course-module" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400">
                            {t("session.course.module")}
                        </label>
                        <select
                            id="session-course-module"
                            className={SELECT_CLASS}
                            value={course.moduleId ?? ""}
                            disabled={saving}
                            onChange={(event) => assign(event.target.value || null)}
                        >
                            <option value="">{t("session.course.noModule")}</option>
                            {options.map((module) => (
                                <option key={module.id} value={module.id}>
                                    {module.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                {course.modules.length === 0 && (
                    <p className="mt-3 text-xs text-content-tertiary">{t("session.course.noModules")}</p>
                )}
                {saveFailed && (
                    <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
                        {t("session.course.saveFailed")}
                    </p>
                )}
            </section>

            {/* The chosen module, in a card of its own: read, not edited, here. */}
            {selected && (
                <section className="glass-effect glass-border border rounded-2xl p-5 sm:p-8 shadow-theme-sm">
                    <ModuleDetail module={selected} />
                </section>
            )}
        </div>
    );
}
